import { lazy, Suspense, useState } from "react";
import { EmptyState, Pagination } from "@/shared/components";
import { Prompt, PromptForm } from "@/shared/ui";
import { useAuth, useLazyMount } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { useIncomeListPage, useIncomeModals, useIncomeMutations } from "../../hooks";
import type { IncomeRecord, IncomeRecordListItem } from "../../types";
import {
  IncomeHeader,
  IncomeStatCards,
  IncomeFilter,
  IncomePageSkeleton,
} from "./components";

const IncomeTable = lazy(() => import("./components/IncomeTable").then((m) => ({ default: m.default })));
const IncomeCreateModal = lazy(() => import("./components/IncomeCreateModal").then((m) => ({ default: m.default })));
const IncomeEditModal = lazy(() => import("./components/IncomeEditModal").then((m) => ({ default: m.default })));
const IncomeDetailsModal = lazy(() => import("./components/IncomeDetailsModal").then((m) => ({ default: m.default })));

export default function IncomePage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(PERMISSIONS.ACCOUNTING.CREATE_INCOME);

  const {
    data,
    isLoading,
    isError,
    filteredRecords,
    pagination,
    pageRecords,
    sortConfig,
    handleSort,
    search,
    setSearch,
    dateRange,
    setDateRange,
    poolFilter,
    setPoolFilter,
    incomeTypeFilter,
    setIncomeTypeFilter,
    poolOptions,
    incomeTypeOptions,
    activeFilters,
    handleResetFilters,
    locale,
    stats,
    t,
  } = useIncomeListPage();

  const {
    createModal,
    setCreateModal,
    editModal,
    setEditModal,
    detailModal,
    setDetailModal,
    confirmDelete,
    setConfirmDelete,
    prompt,
    setPrompt,
  } = useIncomeModals();

  const createModalWasOpened = useLazyMount(createModal);

  const {
    creating,
    deletingId,
    isDeleting,
    isApproving,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleApprove,
  } = useIncomeMutations(setPrompt);

  const [approveRecord, setApproveRecord] = useState<{ id: number } | null>(null);

  const isAnyModalOpen =
    createModal ||
    editModal !== null ||
    detailModal !== null ||
    confirmDelete !== null ||
    approveRecord !== null;

  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <IncomeHeader />
          <IncomePageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <IncomeHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.errorTitle")}</p>
        </section>
      </>
    );
  }

  function handleOpenRecord(record: IncomeRecordListItem) {
    setDetailModal(record);
  }

  function handleEditRecord(record: IncomeRecordListItem) {
    setEditModal(record);
  }

  function handleApproveRecord(record: IncomeRecordListItem | IncomeRecord) {
    setApproveRecord({ id: record.id });
  }

  async function handleApproveConfirm() {
    if (!approveRecord || isApproving) return;
    const result = await handleApprove(approveRecord.id);
    if (result.ok) setApproveRecord(null);
  }

  async function handleDeleteConfirm(id: number) {
    await handleDelete(id);
    setConfirmDelete(null);
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <IncomeHeader />

        <IncomeStatCards stats={stats} />

        <div className="radius-3xl surface-base-fill p-7">
          <IncomeFilter
            search={search}
            onSearchChange={setSearch}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            poolFilter={poolFilter}
            onPoolFilterChange={setPoolFilter}
            incomeTypeFilter={incomeTypeFilter}
            onIncomeTypeFilterChange={setIncomeTypeFilter}
            poolOptions={poolOptions}
            incomeTypeOptions={incomeTypeOptions}
            locale={locale}
            onCreateClick={() => setCreateModal(true)}
            canCreate={canCreate}
            activeFilters={activeFilters}
            onResetFilters={handleResetFilters}
          />

          {filteredRecords.length === 0 ? (
            <EmptyState
              hasActiveFilters={activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              <Suspense fallback={<IncomePageSkeleton variant="tableOnly" />}>
                <IncomeTable
                  records={pageRecords}
                  onOpen={handleOpenRecord}
                  onEdit={handleEditRecord}
                  onApprove={handleApproveRecord}
                  onDelete={(id) => setConfirmDelete(id)}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  deletingId={deletingId}
                  isAnyModalOpen={isAnyModalOpen}
                />
              </Suspense>

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={filteredRecords.length}
              />
            </>
          )}
        </div>
      </section>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="IncomePage"
        />
      )}

      {confirmDelete !== null && (
        <PromptForm
          title={t("confirm.deleteTitle")}
          text={t("confirm.deleteText")}
          variant="error"
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => handleDeleteConfirm(confirmDelete)}
          isLoading={isDeleting}
          confirmText={t("confirm.deleteConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="IncomePage"
        />
      )}

      {createModalWasOpened && (
        <Suspense fallback={null}>
          <IncomeCreateModal
            hidden={!createModal}
            onClose={() => setCreateModal(false)}
            onConfirm={handleCreate}
            isLoading={creating}
            setPrompt={setPrompt}
            showInKindOption
          />
        </Suspense>
      )}

      {editModal && (
        <Suspense fallback={null}>
          <IncomeEditModal
            recordId={editModal.id}
            onClose={() => setEditModal(null)}
            onConfirm={handleUpdate}
            isLoading={false}
          />
        </Suspense>
      )}

      {detailModal && (
        <Suspense fallback={null}>
          <IncomeDetailsModal
            recordId={detailModal.id}
            onClose={() => setDetailModal(null)}
            onApprove={handleApproveRecord}
          />
        </Suspense>
      )}

      {approveRecord && (
        <PromptForm
          title={t("confirm.approveTitle")}
          text={t("confirm.approveText")}
          variant="success"
          onClose={() => setApproveRecord(null)}
          onConfirm={handleApproveConfirm}
          isLoading={isApproving}
          confirmText={t("confirm.approveConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="IncomePage"
        />
      )}
    </>
  );
}
